import React, { useEffect, useState, useMemo } from 'react';
import { Card, Row, Col, Spinner, Alert, Container, Form, Button } from 'react-bootstrap';
import { fetchDailyAttendances, fetchEmployeeCount, fetchTotalCheckinsToday as fetchTotalCheckins, fetchPortWiseAttendanceToday, fetchPortWiseAttendanceByDate, getPMCountForMonth } from './services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { DailyAttendanceData } from './types';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, BarElement, Title, Tooltip, Legend);

const fetchTotalEmployees = async (): Promise<number> => {
  return await fetchEmployeeCount();
};

const fetchTotalCheckinsToday = async (): Promise<number> => {
  return await fetchTotalCheckins();
};

const fetchTotalPMCount = async (): Promise<number> => {
  return await getPMCountForMonth();
};

function useCountUp(target: number | null, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (typeof target !== 'number' || isNaN(target)) return;
    const finalTarget = target;
    let start = 0;
    let startTime: number | null = null;
    function animate(ts: number) {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setCount(Math.floor(progress * (finalTarget - start) + start));
      if (progress < 1) requestAnimationFrame(animate);
      else setCount(finalTarget);
    }
    requestAnimationFrame(animate);
    // eslint-disable-next-line
  }, [target]);
  return count;
}

const Home: React.FC = () => {
  const [totalEmployees, setTotalEmployees] = useState<number | null>(null);
  const [totalCheckins, setTotalCheckins] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [portChartData, setPortChartData] = useState<any>(null);
  const [portChartLoading, setPortChartLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [totalAttendanceCount, setTotalAttendanceCount] = useState<number>(0);
  const [totalPMCount, setTotalPMCount] = useState<number>(0);
  const [portDataRaw, setPortDataRaw] = useState<any[]>([]);
  const navigate = useNavigate();
  const { token } = useAuth();

  const countEmployees = useCountUp(totalEmployees);
  const countCheckins = useCountUp(totalCheckins);

  const rangeLabel = useMemo(() => {
    function toOrdinal(n: number) {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }
    // Show last 7 days excluding today: start = 6 days before yesterday, end = yesterday
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    return `${toOrdinal(start.getDate())} to ${toOrdinal(end.getDate())}`;
  }, []);

  // Create chart options dynamically to access port data in tooltip
  const getPortChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function (context: any) {
            const portIndex = context.dataIndex;
            const percentage = context.parsed.y;
            const count = portDataRaw[portIndex]?.attendance_count || 0;
            return `${count} (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#e0e0e0' },
        ticks: {
          stepSize: 5,
          autoSkip: false,
          precision: 0,
          callback: function (value: any) {
            return value + '%';
          }
        },
        min: 0,
        max: 60
      },
    },
    elements: {
      bar: {
        borderWidth: 0,
        hoverBorderWidth: 0,
        hoverBackgroundColor: '#0369a1'
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false
    }
  } as any);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchTotalEmployees(),
      fetchTotalCheckinsToday(),
      fetchTotalPMCount()
    ])
      .then(([employees, checkins, pmCount]) => {
        setTotalEmployees(employees);
        setTotalCheckins(checkins);
        setTotalPMCount(pmCount);
      })
      .catch(() => setError('Failed to fetch dashboard data'))
      .finally(() => setLoading(false));

    // Fetch real check-in data for last 7 days
    const fetchChart = async () => {
      setChartLoading(true);
      const days: string[] = [];
      // Use the last 7 days excluding today: from 7 days ago up to yesterday
      const end = new Date();
      end.setDate(end.getDate() - 1); // yesterday
      const start = new Date(end);
      start.setDate(end.getDate() - 6); // 6 days before yesterday
      const endDate = end.toISOString().slice(0, 10);
      const startDate = start.toISOString().slice(0, 10);

      // Prepare the days array for labels
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - (i + 1)); // shift by one to exclude today
        days.push(d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }));
      }

      try {
        const result = await fetchDailyAttendances({
          startDate,
          endDate,
          groupByDate: true
        });

        // Create a map to count check-ins per day
        const dailyCounts = new Map<string, number>();

        // Initialize all days with 0 count
        days.forEach(day => {
          dailyCounts.set(day, 0);
        });

        // Count check-ins for each day
        if (Array.isArray(result.data)) {
          result.data.forEach((attendance: DailyAttendanceData) => {
            const attendanceDate = new Date(attendance.attendance_date)
              .toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
            dailyCounts.set(attendanceDate, (dailyCounts.get(attendanceDate) || 0) + 1);
          });
        }

        // Convert the map to array in the order of days
        const counts = days.map(day => dailyCounts.get(day) || 0);

        setChartData({
          labels: days,
          datasets: [
            {
              label: 'Check-ins',
              data: counts,
              borderColor: '#156082',
              backgroundColor: (ctx: any) => {
                const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, 220);
                gradient.addColorStop(0, 'rgba(21, 96, 130, 0.2)');
                gradient.addColorStop(1, 'rgba(232, 244, 248, 0.1)');
                return gradient;
              },
              borderWidth: 3,
              pointBackgroundColor: '#156082',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
              pointRadius: 5,
              pointHoverRadius: 7,
              fill: true,
              tension: 0.4,
            },
          ],
        });
      } catch (error) {
        console.error('Failed to fetch chart data:', error);
      } finally {
        setChartLoading(false);
      }
    };
    fetchChart();
  }, []);

  // Add new useEffect for port-wise attendance
  useEffect(() => {
    const fetchPortWiseData = async () => {
      setPortChartLoading(true);
      try {
        const result = await fetchPortWiseAttendanceByDate(selectedDate);
        const portData = result.data || [];
        const total = portData.reduce((sum: number, item: any) => sum + item.attendance_count, 0);

        // Store the total count and raw data for display
        setTotalAttendanceCount(total);
        setPortDataRaw(portData);

        setPortChartData({
          labels: portData.map((item: any) => item.port_name),
          datasets: [
            {
              label: 'Attendance (%)',
              data: portData.map((item: any) =>
                total > 0 ? Math.round(((item.attendance_count / total) * 100)) : 0
              ),
              backgroundColor: '#0284c7',
              borderRadius: 12,
              maxBarThickness: 36,
            },
          ],
        });
      } catch (error) {
        console.error('Failed to fetch port-wise attendance:', error);
        setTotalAttendanceCount(0);
        setPortDataRaw([]);
      } finally {
        setPortChartLoading(false);
      }
    };

    fetchPortWiseData();
  }, [selectedDate]);

  const currentMonth = new Date().toLocaleString('default', { month: 'long' });

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(120deg,rgb(224, 241, 250) 0%,rgb(224, 244, 255) 100%)', position: 'relative', overflowX: 'hidden', paddingBottom: '10px' }}>
      {/* Decorative SVG background */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 220, zIndex: 0 }} viewBox="0 0 1440 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0,120 Q360,200 720,120 T1440,120 V220 H0 Z" fill="#e8f4f8" fillOpacity="0.5" />
        <circle cx="1200" cy="60" r="80" fill="#156082" fillOpacity="0.08" />
        <circle cx="200" cy="80" r="60" fill="#0284c7" fillOpacity="0.07" />
      </svg>
      <Container fluid className="py-3 px-0 px-md-0" style={{ maxWidth: 1400, position: 'relative', zIndex: 2 }}>
        {/* Chart and Stat Cards Row */}
        <Row className="g-3 align-items-stretch flex-lg-nowrap px-4">
          {/* Stat Cards stacked vertically */}
          <Col xs={12} lg={2} className="d-flex flex-column gap-3 align-items-stretch" style={{ height: 190 }}>
            <Card className="shadow border-0 flex-fill" style={{ background: 'linear-gradient(120deg,#156082 60%, #e8f4f8 100%)', borderRadius: 8, backdropFilter: 'blur(50px)', border: '1.5px solid rgba(21, 96, 130, 0.13)', height: '130px', paddingTop: '10px' }}>
              <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
                <i className="fa-solid fa-users mb-2" style={{ fontSize: 32, opacity: 0.85, textShadow: '0 2px 8px #15608233', color: 'white' }} />
                <div className="fw-bold" style={{ fontSize: 15, color: 'white', textTransform: 'uppercase' }}>Total Employees</div>
                <div className="fw-bold" style={{ fontSize: 28, letterSpacing: 1, color: 'white' }}>{loading ? <Spinner size="sm" /> : countEmployees}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} lg={2} className="d-flex flex-column gap-3 align-items-stretch" style={{ height: 190 }}>
            <Card className="shadow border-0 flex-fill" style={{ background: 'linear-gradient(120deg, #0284c7 60%, #bae6fd 100%)', borderRadius: 8, backdropFilter: 'blur(10px)', border: '1.5px solid rgba(2, 132, 199, 0.13)', height: '130px', paddingTop: '10px' }}>
              <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
                <i className="fa-solid fa-user-check mb-2" style={{ fontSize: 32, opacity: 0.85, textShadow: '0 2px 8px #0284c733', color: 'white' }} />
                <div className="fw-bold" style={{ fontSize: 15, color: 'white', textTransform: 'uppercase' }}>Check-ins Today</div>
                <div className="fw-bold" style={{ fontSize: 28, letterSpacing: 1, color: 'white' }}>{loading ? <Spinner size="sm" /> : countCheckins}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} lg={2} className="d-flex flex-column gap-3 align-items-stretch" style={{ height: 190 }}>
            <Card className="shadow border-0 flex-fill" style={{ background: 'linear-gradient(120deg, #009e05ff 60%, #bafdc7ff 100%)', borderRadius: 8, backdropFilter: 'blur(10px)', border: '1.5px solid rgba(2, 132, 199, 0.13)', height: '130px', paddingTop: '10px' }}>
              <Card.Body className="d-flex flex-column align-items-center justify-content-center p-3">
                <i className="fas fa-clipboard-list mb-2" style={{ fontSize: 32, opacity: 0.85, textShadow: '0 2px 8px #0284c733', color: 'white' }} />
                <div className="fw-bold" style={{ fontSize: 15, color: 'white', textTransform: 'uppercase' }}>Total PM</div>
                <div className="fw-bold" style={{ fontSize: 15, color: 'white', textTransform: 'uppercase' }}>({currentMonth})</div>
                <div className="fw-bold" style={{ fontSize: 28, letterSpacing: 1, color: 'white' }}>{loading ? <Spinner size="sm" /> : totalPMCount}</div>
              </Card.Body>
            </Card>
          </Col>
          {/* Chart Card */}
          <Col xs={12} lg={6} className="mb-0 mb-lg-0 d-flex align-items-stretch">
            <Card className="glass-card shadow-lg border-0 w-100 p-2" style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 8 }}>
              <Card.Body className="p-1">
                <div className="fw-bold mb-1 text-center" style={{ color: '#156082', fontSize: 14, letterSpacing: 1, textTransform: 'uppercase' }}>Check-ins (Last Week)</div>
                {chartLoading ? (
                  <div className="chart-skeleton-line d-flex align-items-center justify-content-center" style={{ width: '100%', height: 70, marginTop: 8 }}>
                    <div className="skeleton-line-chart" style={{
                      width: '90%',
                      height: '50%',
                      background: 'linear-gradient(90deg, #e0e0e0 25%, #f3f3f3 37%, #e0e0e0 63%)',
                      backgroundSize: '400% 100%',
                      animation: 'skeleton-loading 1.2s ease-in-out infinite',
                      borderRadius: '4px',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '20%',
                        left: '10%',
                        width: '80%',
                        height: '2px',
                        background: '#d0d0d0',
                        borderRadius: '1px'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '20%',
                        left: '15%',
                        width: '6px',
                        height: '6px',
                        background: '#c0c0c0',
                        borderRadius: '50%'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '35%',
                        left: '30%',
                        width: '6px',
                        height: '6px',
                        background: '#c0c0c0',
                        borderRadius: '50%'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '10%',
                        left: '50%',
                        width: '6px',
                        height: '6px',
                        background: '#c0c0c0',
                        borderRadius: '50%'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '25%',
                        left: '70%',
                        width: '6px',
                        height: '6px',
                        background: '#c0c0c0',
                        borderRadius: '50%'
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '40%',
                        left: '85%',
                        width: '6px',
                        height: '6px',
                        background: '#c0c0c0',
                        borderRadius: '50%'
                      }}></div>
                    </div>
                  </div>
                ) : chartData && (
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                        title: { display: false },
                      },
                      scales: {
                        x: { grid: { display: false } },
                        y: { beginAtZero: true, grid: { color: '#e0e0e0' } },
                      },
                      elements: {
                        point: {
                          hoverRadius: 8,
                        },
                        line: {
                          tension: 0.4,
                        },
                      },
                    }}
                    height={70}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>


        </Row>
        <Row className="g-2 justify-content-center align-items-stretch px-4">
          {/* Port-wise Attendance Chart */}
          <Col xs={12} className="mb-12">
            <Card className="glass-card shadow-lg border-0 w-100 p-2" style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 8 }}>
              <Card.Body className="p-1">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="fw-bold" style={{ color: '#0284c7', fontSize: 14, letterSpacing: 1, textTransform: 'uppercase' }}>
                    Location-wise Attendance
                  </div>
                  <div className="fw-bold" style={{ color: '#0284c7', fontSize: 14, letterSpacing: 1, textTransform: 'uppercase' }}>
                    TOTAL APPROVED MANPOWER - 607
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ color: '#0284c7', fontSize: 14, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 900 }}>
                      Total Attendance: {totalAttendanceCount}
                    </div>
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        width: '140px'
                      }}
                    />
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setSelectedDate(new Date().toISOString().slice(0, 10))}
                      style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        borderColor: '#0284c7',
                        color: '#0284c7'
                      }}
                    >
                      Today
                    </Button>
                  </div>
                </div>
                {portChartLoading ? (
                  <div className="chart-skeleton-bars d-flex align-items-end justify-content-between" style={{ width: '100%', height: 120, marginTop: 8 }}>
                    <div className="chart-skeleton-bar" style={{ height: '40%' }} />
                    <div className="chart-skeleton-bar" style={{ height: '70%' }} />
                    <div className="chart-skeleton-bar" style={{ height: '55%' }} />
                    <div className="chart-skeleton-bar" style={{ height: '80%' }} />
                    <div className="chart-skeleton-bar" style={{ height: '60%' }} />
                  </div>
                ) : portChartData && (
                  <div style={{ height: '450px', width: '100%' }}>
                    <Bar
                      data={portChartData}
                      options={getPortChartOptions()}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {/* Quick Action Cards */}
        <Row className="g-4 justify-content-center align-items-stretch px-4">
          <Col xs={12} sm={6} md={6} xl={3} className="d-flex align-items-stretch">
            <Card className="h-100 shadow-lg border-0 text-center clickable report-card glass-card quick-action-card d-flex flex-column justify-content-center align-items-center" style={{ cursor: 'pointer', minHeight: 120, background: 'linear-gradient(120deg, #f0fff0 60%, #d1fae5 100%)', borderRadius: 8, width: '100%' }} onClick={() => navigate('/daily-attendance')}>
              <Card.Body className="d-flex flex-column align-items-center justify-content-center h-100 p-2">
                <i className="fa-solid fa-calendar-check" style={{ fontSize: 26, color: '#156082', marginBottom: 8, textShadow: '0 2px 8px #15608222' }} />
                <Card.Title style={{ fontSize: 16, color: '#156082', fontWeight: 600, textTransform: 'uppercase' }}>Daily Attendance</Card.Title>
                <div className="text-muted" style={{ fontSize: 13, textTransform: 'uppercase' }}>View and analyze daily attendance</div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} xl={3} className="d-flex align-items-stretch">
            <Card className="h-100 shadow-lg border-0 text-center clickable report-card glass-card quick-action-card d-flex flex-column justify-content-center align-items-center" style={{ cursor: 'pointer', minHeight: 120, background: 'linear-gradient(120deg, #e0f2fe 60%, #bae6fd 100%)', borderRadius: 8, width: '100%' }} onClick={() => navigate('/location-tracking')}>
              <Card.Body className="d-flex flex-column align-items-center justify-content-center h-100 p-2">
                <i className="fa-solid fa-map-location-dot" style={{ fontSize: 26, color: '#0284c7', marginBottom: 8, textShadow: '0 2px 8px #0284c722' }} />
                <Card.Title style={{ fontSize: 16, color: '#156082', fontWeight: 600, textTransform: 'uppercase' }}>Geofence Map users</Card.Title>
                <div className="text-muted" style={{ fontSize: 13, textTransform: 'uppercase' }}>Track employee locations</div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} xl={3} className="d-flex align-items-stretch">
            {token && token.designation?.user_type === 'ADMIN' ? (
              <Card className="h-100 shadow-lg border-0 text-center clickable report-card glass-card quick-action-card d-flex flex-column justify-content-center align-items-center" style={{ cursor: 'pointer', minHeight: 120, background: 'linear-gradient(120deg, #fef9c3 60%, #fde68a 100%)', borderRadius: 8, width: '100%' }} onClick={() => navigate('/terminals')}>
                <Card.Body className="d-flex flex-column align-items-center justify-content-center h-100 p-2">
                  <i className="fa-solid fa-network-wired" style={{ fontSize: 26, color: '#eab308', marginBottom: 8, textShadow: '0 2px 8px #eab30822' }} />
                  <Card.Title style={{ fontSize: 16, color: '#156082', fontWeight: 600, textTransform: 'uppercase' }}>Manage Terminal</Card.Title>
                  <div className="text-muted" style={{ fontSize: 13, textTransform: 'uppercase' }}>View, add, and edit terminals</div>
                </Card.Body>
              </Card>
            ) : (
              <Card className="h-100 shadow-lg border-0 text-center clickable report-card glass-card quick-action-card d-flex flex-column justify-content-center align-items-center" style={{ cursor: 'pointer', minHeight: 120, background: 'linear-gradient(120deg, #fef9c3 60%, #fde68a 100%)', borderRadius: 8, width: '100%' }} onClick={() => navigate('/ports')}>
                <Card.Body className="d-flex flex-column align-items-center justify-content-center h-100 p-2">
                  <i className="fa-solid fa-ship" style={{ fontSize: 26, color: '#eab308', marginBottom: 8, textShadow: '0 2px 8px #eab30822' }} />
                  <Card.Title style={{ fontSize: 16, color: '#156082', fontWeight: 600, textTransform: 'uppercase' }}>Manage Locations</Card.Title>
                  <div className="text-muted" style={{ fontSize: 13, textTransform: 'uppercase' }}>View, add, and edit ports</div>
                </Card.Body>
              </Card>
            )}
          </Col>
          <Col xs={12} sm={6} md={6} xl={3} className="d-flex align-items-stretch">
            <Card className="h-100 shadow-lg border-0 text-center clickable report-card glass-card quick-action-card d-flex flex-column justify-content-center align-items-center" style={{ cursor: 'pointer', minHeight: 120, background: 'linear-gradient(120deg, #f0abfc 60%, #e879f9 100%)', borderRadius: 8, width: '100%' }} onClick={() => navigate('/employees')}>
              <Card.Body className="d-flex flex-column align-items-center justify-content-center h-100 p-2">
                <i className="fa-solid fa-users" style={{ fontSize: 26, color: '#a21caf', marginBottom: 8, textShadow: '0 2px 8px #a21caf22' }} />
                <Card.Title style={{ fontSize: 16, color: '#156082', fontWeight: 600, textTransform: 'uppercase' }}>Manage Employees</Card.Title>
                <div className="text-muted" style={{ fontSize: 13, textTransform: 'uppercase' }}>View, add, and edit employees</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {error && <Alert variant="danger">{error}</Alert>}
      </Container>
      <style>{`
        .hero-logo-glow {
          box-shadow: 0 0 0 6px #e8f4f8aa, 0 2px 12px #15608222;
          border-radius: 50%;
          display: inline-block;
        }
        .glass-card {
          background: rgba(255,255,255,0.7) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
                     border: 1.5px solid rgba(21, 96, 130, 0.13);
        }
        .quick-action-card {
          transition: transform 0.18s cubic-bezier(.4,2,.6,1), box-shadow 0.18s;
        }
        .quick-action-card:hover {
          transform: scale(1.035) translateY(-2px);
          box-shadow: 0 4px 16px #15608222, 0 1px 4px #0284c722;
          z-index: 2;
          filter: brightness(1.04);
        }
        @media (max-width: 991px) {
          .sticky-lg-top {
            position: static !important;
          }
        }
        .chart-skeleton-bars {
          width: 100%;
          height: 70px;
          display: flex;
          align-items: end;
          justify-content: space-between;
          gap: 6px;
        }
        .chart-skeleton-bar {
          width: 12%;
          min-width: 16px;
          border-radius: 6px 6px 0 0;
          background: linear-gradient(90deg, #e0e0e0 25%, #f3f3f3 37%, #e0e0e0 63%);
          background-size: 400% 100%;
          animation: skeleton-loading 1.2s ease-in-out infinite;
        }
        @keyframes skeleton-loading {
          0% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0 50%;
          }
        }
        .hero-card-bg {
          background: linear-gradient(120deg, #e0ffe0 0%, #e0f7fa 100%) !important;
        }
      `}</style>
    </div>
  );
}

export default Home; 